import { InputNumber, Modal, message } from "antd";
import useAuthStore from "../../stores/useAuthStore";
import { resolveActingUserId } from "../../utils/actingUser";
import { trace } from "../../utils/debugTrace";
import { closePendingTillSession } from "../../services/tillCloseService";
import { checkTillMoved, closeTillPendingNotice } from "../TillMovedGuard";

// BUG-2582: Open Till answered "Close Till is pending for this Till". The server still has
// this till's day open and this browser has no record of it (the POS reaches Open Till only
// when no open till event is stored here), so this browser's own Close Till can never run.
//
// When the server says this browser holds the till (its registration, or a row with its
// device id), no other place can be billing on it -- the backend refuses a retail till
// while another registration is active -- so the cashier may close that day here with the
// cash counted in the drawer, and the till opens again. Any other answer (another device
// holds it, rows without device id, offline) keeps the notice: Close Till where it was
// opened, or back office.
const RECOVERY_STATES = ["held", "own_row"];

// true when the answer was handled here (notice or recovery shown); false keeps the
// caller's own message (any other answer, or a till that cannot move).
// date: the tillCash date the refused Open Till sent (the backend finds the day by it).
// onClosed: runs Open Till again.
export async function handleCloseTillPending({ serverMessage, date, onClosed }) {
  const notice = closeTillPendingNotice(serverMessage);
  if (!notice) return false;
  let hold;
  try {
    hold = await checkTillMoved();
  } catch (err) {
    hold = { state: "not_checked", error: err?.message };
  }
  trace("OpenTill", "Close Till is pending: till hold", { state: hold?.state, error: hold?.error });
  if (!RECOVERY_STATES.includes(hold?.state)) {
    Modal.warning({ ...notice, centered: true });
    return true;
  }
  showPendingCloseDialog({ date, onClosed });
  return true;
}

export const PENDING_CLOSE_TITLE = "This till's previous day is still open";

const isCountedCash = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v)) && Number(v) >= 0;

// A static Modal.confirm re-throws a rejected onOk as an unhandled rejection, so onOk never
// rejects here: an empty amount keeps the button disabled, and a refused or failed close
// shows its message and opens the dialog again with the amount kept.
function showPendingCloseDialog({ date, onClosed, initialCash = null }) {
  const tillData = useAuthStore.getState().tillData;
  let counted = initialCash;
  const modal = Modal.confirm({
    title: PENDING_CLOSE_TITLE,
    centered: true,
    okText: "Close previous day",
    cancelText: "Cancel",
    okButtonProps: { disabled: !isCountedCash(counted) },
    content: (
      <div data-testid="pending-close-dialog">
        <p>
          The server still has this till's day open, but this browser has no record of it (its data was cleared). Bills of that day that reached the server are kept; bills saved
          here that had not reached the server cannot be sent.
        </p>
        <p>Close that day here only if no other browser or device is using this till. Count the cash in the drawer and enter it below.</p>
        <InputNumber
          data-testid="pending-close-cash"
          min={0}
          style={{ width: "100%" }}
          placeholder="Cash in the drawer now"
          defaultValue={initialCash ?? undefined}
          onChange={(v) => {
            counted = v;
            modal?.update({ okButtonProps: { disabled: !isCountedCash(v) } });
          }}
        />
      </div>
    ),
    onOk: async () => {
      if (!isCountedCash(counted)) {
        message.error("Enter the cash in the drawer (0 or more).");
        setTimeout(() => showPendingCloseDialog({ date, onClosed }), 0);
        return;
      }
      const amount = Number(counted);
      const res = await closePendingTillSession({ tillData, date, countedCash: amount, userId: resolveActingUserId(tillData) });
      if (!res.ok) {
        message.error(res.message);
        setTimeout(() => showPendingCloseDialog({ date, onClosed, initialCash: amount }), 0);
        return;
      }
      message.success("Previous day closed. Opening the till.");
      // After the dialog closes: a failure of the new open is that screen's own message,
      // and the close above must not be sent twice.
      if (typeof onClosed === "function") {
        setTimeout(() => {
          Promise.resolve()
            .then(onClosed)
            .catch((err) => trace("OpenTill", "Open Till after the previous day's close failed", { message: err?.message }));
        }, 0);
      }
    },
  });
}
